import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import { parametresVueGlobale } from './useVueGlobale';

/**
 * Apprenants à risque (TF-427) : les apprenants en formation dont le taux
 * d'absentéisme dépasse le seuil de l'organisme. Le taux, la tendance, le seuil
 * de la formation et le courrier sont des lectures de l'API
 * (`domain/apprenants-risque.ts`) : l'écran affiche, il ne recalcule rien.
 */

export type AffichageRisque = 'RISQUE' | 'TOUS';

export interface Absenteisme {
  taux: number | null;
  demiJournees: number;
  absences: number;
  justifiees: number;
}

export interface LigneApprenantRisque {
  id: string;
  nom: string;
  prenom: string;
  initiales: string;
  siteCode: string;
  formation: { code: string; titre: string } | null;
  promotion: { cle: string; libelle: string };
  referent: { id: string; nom: string } | null;
  entreprise: string | null;
  absenteisme: Absenteisme;
  tendance: Absenteisme & { auDelaDuSeuil: boolean };
  aRisque: boolean;
  derniereAbsence: string | null;
  seuilFormation: {
    seuil: number;
    nonJustifiees: number;
    atteint: boolean;
    courrierDu: number | null;
    dernierCourrier: { palier: number; le: string } | null;
  };
}

export interface SeuilAbsenteisme {
  valeur: number;
  parDefaut: boolean;
  min: number;
  max: number;
  regleLe: string | null;
  reglePar: string | null;
}

export interface ReponseApprenantsRisque {
  calculeLe: string;
  seuil: SeuilAbsenteisme;
  fenetreTendanceJours: number;
  compteurs: {
    suivis: number;
    aRisque: number;
    sansEmargement: number;
    absenteisme: Absenteisme;
    seuilFormationAtteint: number;
  };
  options: {
    formations: { code: string; titre: string }[];
    promotions: { cle: string; libelle: string; siteCode: string; formation: string | null }[];
  };
  apprenants: LigneApprenantRisque[];
}

export interface FiltresRisque {
  site: string;
  afficher: AffichageRisque;
  formation: string;
  promotion: string;
  recherche: string;
}

const CLE = ['apprenants-risque'];

export function useApprenantsRisque(filtres: FiltresRisque) {
  const chaine = parametresVueGlobale({ ...filtres });
  return useQuery({
    queryKey: [...CLE, chaine],
    queryFn: () => appelApi<ReponseApprenantsRisque>(`/apprenants-risque?${chaine}`),
    placeholderData: keepPreviousData,
  });
}

export function useReglerSeuilAbsenteisme() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (seuil: number) =>
      appelApi<SeuilAbsenteisme>('/apprenants-risque/seuil', { methode: 'PUT', corps: { seuil } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CLE });
      // Le pipeline du tableau de bord compte les apprenants à risque.
      void client.invalidateQueries({ queryKey: ['financements', 'tableau-de-bord-direction'] });
    },
  });
}
